import type React from 'react';

import type * as api from '@gitbook/api-client';
import { DateShortRelative } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { CardLink } from '@gitbook/spine-react';

import { CollectionIcon, type OrgAllContentItem, SpaceIcon } from '../../../../Components';
import { SiteIcon } from '../../../../Components/Site/SiteIcon';

/**
 * A card to display a content item (space, change request).
 */
export function ContentCard({
    content,
    organizationId,
}: {
    content: OrgAllContentItem<api.OrganizationAllSite | api.OrganizationAllSpace>;
    organizationId: string;
}): React.ReactElement {
    const { item, ancestors } = content;

    return (
        <CardLink
            className="flex h-[8.125rem] flex-1 flex-col justify-between gap-3 p-6"
            href={
                'site' in item
                    ? {
                          screen: {
                              type: 'site',
                              site: item.site.id,
                              organization: organizationId,
                          },
                      }
                    : {
                          screen: {
                              type: 'space',
                              space: item.space.id,
                              organization: organizationId,
                          },
                      }
            }
            aria-label={
                'site' in item ? `Go to site ${item.site.title}` : `Go to space ${item.space.title}`
            }
        >
            <div className="flex flex-col gap-3">
                <header className="truncate text-muted text-ui-small">
                    <p className="flex items-center">
                        In
                        <span className="m-nudge-icon mx-1 flex shrink-0">
                            {'site' in item ? (
                                <Icon.AllDocsSites size={14} />
                            ) : (
                                <>
                                    {ancestors.length ? (
                                        <CollectionIcon size={14} />
                                    ) : (
                                        <Icon.Documentation size={14} />
                                    )}
                                </>
                            )}
                        </span>
                        {'site' in item
                            ? 'Docs sites'
                            : ancestors[0]?.collection.title || 'Documentation'}
                    </p>
                </header>

                <section className="flex items-center gap-2">
                    <div className="m-nudge-icon flex shrink-0">
                        {'site' in item ? (
                            <SiteIcon size={18} site={item.site} />
                        ) : (
                            <SpaceIcon space={item.space} size={18} />
                        )}
                    </div>
                    <p className="max-w-full truncate text-ui-heading-small">
                        {'site' in item ? item.site.title : item.space.title}
                    </p>
                </section>
            </div>

            {'site' in item ? null : (
                <footer className="flex items-center gap-2">
                    <p className="truncate text-light text-ui-small">
                        Last edited{' '}
                        <DateShortRelative
                            value={new Date(item.space.updatedAt)}
                            onlyDate={false}
                        />
                    </p>
                </footer>
            )}
        </CardLink>
    );
}
